import { useCallback, useEffect, useRef, useState } from "react";
import { chatApi, ChatApiError } from "../apis/chat";
import { Storage } from "../storage";

export type VenueAssistantRole = "user" | "assistant";

export type VenueAssistantMessage = {
  id: string;
  role: VenueAssistantRole;
  content: string;
  isError?: boolean;
  // Optional longer breakdown revealed via a "show more" toggle (assistant only).
  details?: string;
  // Label for that toggle, e.g. "Show space-by-space breakdown".
  detailsLabel?: string;
};

/** Discriminates the kind of failure so the UI can show the right copy. */
export type VenueAssistantError = "unavailable" | "error" | null;

const VISITOR_ID_KEY = "chatVisitorId";
const TRANSCRIPT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Stable anonymous id, persisted across visits. Used to (a) namespace the saved
 * transcript per browser and (b) tag requests for backend logging/analytics.
 */
function resolveVisitorId(): string {
  const existing = Storage.getString(VISITOR_ID_KEY);
  if (existing) {
    return existing;
  }
  const id = `visitor-${makeId()}`;
  Storage.setString(VISITOR_ID_KEY, id);
  return id;
}

function transcriptKey(visitorId: string, venueSlug: string): string {
  return `venueAssistant:${visitorId}:${venueSlug}`;
}

type Transcript = {
  messages: VenueAssistantMessage[];
  sessionId: string | null;
  savedAt?: number; // epoch ms — used for TTL eviction
};

const EMPTY_TRANSCRIPT: Transcript = { messages: [], sessionId: null };

/** Read a persisted transcript for (visitorId, venueSlug). Drops it silently if older than TRANSCRIPT_TTL_MS. */
function readTranscript(visitorId: string, venueSlug: string): Transcript {
  const key = transcriptKey(visitorId, venueSlug);
  const raw = Storage.getString(key);
  if (!raw) {
    return EMPTY_TRANSCRIPT;
  }
  try {
    const parsed = JSON.parse(raw) as Transcript;
    if (
      parsed.savedAt !== undefined &&
      Date.now() - parsed.savedAt > TRANSCRIPT_TTL_MS
    ) {
      Storage.delete(key);
      return EMPTY_TRANSCRIPT;
    }
    return {
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      sessionId: parsed.sessionId ?? null,
    };
  } catch {
    return EMPTY_TRANSCRIPT;
  }
}

/**
 * Venue assistant chat state. Manages the message transcript, sessionId and a
 * stable visitorId, and persists the transcript to localStorage keyed by
 * (visitorId, venueSlug) so it survives reloads and stays clean per venue.
 *
 * Persistence is imperative (on send / answer / clear) and hydration is
 * synchronous (lazy initial state), so there's no load/persist race that could
 * wipe a saved transcript on remount.
 *
 * Public — works for logged-out visitors.
 */
export function useVenueAssistant(venueSlug: string) {
  const [visitorId] = useState<string>(() => resolveVisitorId());
  const [transcript, setTranscript] = useState<Transcript>(() =>
    readTranscript(visitorId, venueSlug),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<VenueAssistantError>(null);
  // Guided-chat follow-ups for the latest answer. Transient (not persisted):
  // they belong to the most recent assistant turn only.
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Mirror of the latest transcript so async flows read fresh values without
  // re-creating callbacks on every keystroke/message.
  const transcriptRef = useRef(transcript);
  transcriptRef.current = transcript;

  const lastQueryRef = useRef<string | null>(null);
  const isFirstRender = useRef(true);

  // Reload when the venue changes (clean, per-venue thread). The first render is
  // already hydrated by the lazy initializer above, so skip it.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setError(null);
    lastQueryRef.current = null;
    setSuggestions([]);
    setTranscript(readTranscript(visitorId, venueSlug));
  }, [venueSlug, visitorId]);

  const persist = useCallback(
    (next: Transcript) => {
      const key = transcriptKey(visitorId, venueSlug);
      if (next.messages.length === 0) {
        Storage.delete(key);
      } else {
        Storage.setString(key, JSON.stringify({ ...next, savedAt: Date.now() }));
      }
    },
    [venueSlug, visitorId],
  );

  // Apply a new transcript to both state and storage atomically.
  const apply = useCallback(
    (next: Transcript) => {
      transcriptRef.current = next;
      setTranscript(next);
      persist(next);
    },
    [persist],
  );

  const runAsk = useCallback(
    async (query: string, appendUserMessage: boolean) => {
      setError(null);

      if (appendUserMessage) {
        apply({
          ...transcriptRef.current,
          messages: [
            ...transcriptRef.current.messages,
            { id: makeId(), role: "user", content: query },
          ],
        });
      }

      // Hide stale chips while the new answer is in flight.
      setSuggestions([]);
      setIsLoading(true);
      try {
        const result = await chatApi.venueAssistant.ask({
          slug: venueSlug,
          query,
          sessionId: transcriptRef.current.sessionId,
          visitorId,
        });

        apply({
          messages: [
            ...transcriptRef.current.messages,
            {
              id: makeId(),
              role: "assistant",
              content: result.answer,
              details: result.details?.trim() || undefined,
              detailsLabel: result.detailsLabel?.trim() || undefined,
            },
          ],
          sessionId: result.metadata?.sessionId ?? transcriptRef.current.sessionId,
        });
        setSuggestions(
          Array.isArray(result.suggestions) ? result.suggestions : [],
        );
      } catch (err) {
        const status = (err as ChatApiError)?.status;
        setError(status === 503 ? "unavailable" : "error");
      } finally {
        setIsLoading(false);
      }
    },
    [apply, venueSlug, visitorId],
  );

  const send = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (!trimmed || isLoading) {
        return;
      }
      lastQueryRef.current = trimmed;
      void runAsk(trimmed, true);
    },
    [isLoading, runAsk],
  );

  const retry = useCallback(() => {
    if (!lastQueryRef.current || isLoading) {
      return;
    }
    void runAsk(lastQueryRef.current, false);
  }, [isLoading, runAsk]);

  const clear = useCallback(async () => {
    const previousSessionId = transcriptRef.current.sessionId;
    apply(EMPTY_TRANSCRIPT);
    setError(null);
    lastQueryRef.current = null;
    setSuggestions([]);
    await chatApi.venueAssistant.clear({
      sessionId: previousSessionId,
      visitorId,
    });
  }, [apply, visitorId]);

  return {
    messages: transcript.messages,
    sessionId: transcript.sessionId,
    visitorId,
    isLoading,
    error,
    suggestions,
    send,
    retry,
    clear,
  };
}
